import { ReactNode, createContext, useContext } from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { PersonStatusIndicator } from '../../../schedule-calendar-components/appointment-event-card/person-status-indicator';
import { EVENT_CARD_COLOR_LINE_WIDTH } from '../../constants';

type GridEventCardContext = {
  primaryColor: string;
  backgroundColor: string;
};

const GridEventCardContext = createContext<GridEventCardContext | null>(null);

const useGridEventCardContext = () => {
  const context = useContext(GridEventCardContext);
  if (!context) {
    throw new Error('useGridEventCardContext must be used within GridEventCardBase');
  }
  return context;
};

type GridEventCardBaseProps = {
  children: ReactNode;
  primaryColor?: string;
  backgroundColor?: string;
};

const GridEventCardBaseProvider = ({
  children,
  primaryColor = theme.colors.primary50,
  backgroundColor = theme.colors.white,
}: GridEventCardBaseProps) => {
  return (
    <GridEventCardContext.Provider
      value={{
        primaryColor,
        backgroundColor,
      }}
    >
      {children}
    </GridEventCardContext.Provider>
  );
};

// Sub-components
const EventColoredLine = ({ isRequest = false }: { isRequest?: boolean }) => {
  const { primaryColor } = useGridEventCardContext();
  return (
    <div
      style={{
        position: 'absolute',
        background: primaryColor,
        left: isRequest ? '2px' : 0,
        top: 0,
        width: EVENT_CARD_COLOR_LINE_WIDTH,
        height: '100%',
      }}
    />
  );
};

const PersonStatusBar = ({ personStatus }: { personStatus?: string }) => {
  return <PersonStatusIndicator personStatus={personStatus} height={4} borderRadius='small' isExpanded={false} />;
};

const ArticleShell = ({ children, isRequest = false }: { children: ReactNode; isRequest?: boolean }) => {
  const { backgroundColor } = useGridEventCardContext();

  return (
    <article
      css={[
        css`
          border-radius: ${theme.borderRadius.small};
          padding: ${theme.spacing(0.5, 0.5, 0, 1.5)};
          box-shadow: ${theme.shadows.light};
          position: relative;
          overflow: hidden;
          outline: none;
          background: ${backgroundColor};
          height: 100%;
          transition: box-shadow 0.2s ease-in-out;
          &:hover {
            box-shadow: ${theme.shadows.floating};
          }
        `,
        isRequest &&
          css`
            opacity: 1;
            background: white;
            background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='${encodeURIComponent(
              theme.colors.primary50
            )}' stroke-width='4' stroke-dasharray='10%2c 8' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
            &:hover,
            &:active {
              background-color: ${backgroundColor};
            }
          `,
      ]}
    >
      {children}
    </article>
  );
};

// Export compound component
export const GridEventCardBase = Object.assign(GridEventCardBaseProvider, {
  EventColoredLine,
  PersonStatusBar,
  ArticleShell,
});
